<template>
  <div class="control-widget-bar">
    <!-- Left Section: Site/Project Toggle -->
    <div class="control-widget-bar__left">
      <SiteProjectToggle
        :visible-site-count="visibleSiteCount"
        :total-site-count="totalSiteCount"
        :projects="projects"
        :loading-projects="loadingProjects"
        @mode-changed="onModeChanged"
        @project-selected="onProjectSelected"
      />
    </div>
    
    <!-- Right Section: Additional Controls + Map Controls Button -->
    <div class="control-widget-bar__right">
      <!-- Slot for additional controls (e.g., sort controls) -->
      <slot name="additional-controls"></slot>

      <!-- Active Filters Button (only when dataset filters are active) -->
      <button
        v-if="activeFiltersCount > 0"
        class="map-controls-button active-filters-button"
        :class="{ 'map-controls-button--active': activeFiltersPanelOpen }"
        @click="toggleActiveFiltersPanel"
        :aria-expanded="activeFiltersPanelOpen"
        aria-label="Toggle active filters"
      >
        <ThemeIcon
          icon="filter"
          type="icons"
          classes="w-5 h-5"
        />
        <span class="map-controls-button__text">Active Filters</span>
        <span
          class="map-controls-button__badge map-controls-button__badge--green"
          :aria-label="`${activeFiltersCount} active filters`"
        >
          {{ activeFiltersCount }}
        </span>
      </button>

      <button
        class="map-controls-button"
        :class="{ 'map-controls-button--active': !controlPanelCollapsed && !activeFiltersPanelOpen }"
        @click="toggleControlPanel"
        :aria-expanded="!controlPanelCollapsed"
        aria-label="Toggle map controls"
      >
        <ThemeIcon 
          icon="settings" 
          type="icons"
          classes="w-5 h-5"
        />
        <span class="map-controls-button__text">Map Controls</span>
        <span 
          v-if="activeLayersCount > 0" 
          class="map-controls-button__badge"
          :aria-label="`${activeLayersCount} active layers`"
        >
          {{ activeLayersCount }}
        </span>
      </button>
    </div>
  </div>
</template>

<script>
import SiteProjectToggle from './SiteProjectToggle.vue';
import ThemeIcon from '../ThemeIcon.vue';

export default {
  name: 'ControlWidgetBar',

  components: {
    SiteProjectToggle,
    ThemeIcon
  },

  props: {
    /**
     * Number of visible sites on the map
     */
    visibleSiteCount: {
      type: Number,
      default: 0
    },

    /**
     * Total number of sites available
     */
    totalSiteCount: {
      type: Number,
      default: 0
    },

    /**
     * Array of available projects
     */
    projects: {
      type: Array,
      default: () => []
    },

    /**
     * Whether projects are currently loading
     */
    loadingProjects: {
      type: Boolean,
      default: false
    },

    /**
     * Whether the control panel is collapsed
     */
    controlPanelCollapsed: {
      type: Boolean,
      default: false
    },

    /**
     * Number of active layers
     */
    activeLayersCount: {
      type: Number,
      default: 0
    },

    /**
     * Number of active dataset filters
     */
    activeFiltersCount: {
      type: Number,
      default: 0
    },

    /**
     * Whether the active filters panel is open
     */
    activeFiltersPanelOpen: {
      type: Boolean,
      default: false
    }
  },

  methods: {
    /**
     * Handle mode change event from SiteProjectToggle
     * @param {string} mode - The selected mode ('sites' or 'projects')
     */
    onModeChanged(mode) {
      this.$emit('mode-changed', mode);
    },

    /**
     * Handle project selection event from SiteProjectToggle
     * @param {number|null} projectId - The selected project ID
     */
    onProjectSelected(projectId) {
      this.$emit('project-selected', projectId);
    },

    /**
     * Toggle the control panel visibility
     */
    toggleControlPanel() {
      this.$emit('toggle-control-panel');
    },

    /**
     * Toggle the active filters panel
     */
    toggleActiveFiltersPanel() {
      this.$emit('toggle-active-filters-panel');
    }
  }
};
</script>
